// Use Netlify function as proxy to avoid CORS issues
const CLAUDE_API_URL = '/.netlify/functions/claude-api';

export async function generateWeeklyPlan(weeklyData, userProfile) {
  const prompt = buildWeeklyPlanPrompt(weeklyData, userProfile);

  try {
    const response = await fetch(CLAUDE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 4096,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to generate weekly plan from Claude');
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    console.error('Claude API error:', error);
    throw error;
  }
}

function buildWeeklyPlanPrompt(weeklyData, userProfile) {
  const {
    lastWeekWorkouts,
    missedWorkouts,
    measurements,
    measurementHistory,
    currentCheckIn,
    nutritionSummary,
    nutritionGoals,
    // tier1LastWeek is not used directly (data comes from lastWeekWorkouts)
    tier2Last4Weeks,
    tier3Last12Weeks,
    tier4SinceStart
  } = weeklyData;

  // Helper functions for formatting
  const formatHeight = (inches) => {
    if (!inches) return "N/A";
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  const formatGoal = (goal) => {
    const goalMap = {
      'strength': 'Build Functional Strength',
      'muscle': 'Build Muscle Mass',
      'toned': 'Get Toned & Defined',
      'athletic': 'Improve Athletic Performance',
      'weight_loss': 'Lose Weight & Get Fit',
      'general_fitness': 'General Fitness & Health'
    };
    return goalMap[goal] || goal;
  };

  const formatDuration = (duration) => {
    const durationMap = {
      'under_30': 'Under 30 minutes',
      '30_45': '30-45 minutes',
      '45_60': '45-60 minutes',
      '60_plus': '60+ minutes'
    };
    return durationMap[duration] || duration || '45-60 minutes';
  };

  const formatTime = (time) => {
    const timeMap = {
      'early_morning': 'Early Morning (5-7 AM)',
      'morning': 'Morning (7-10 AM)',
      'midday': 'Midday (10 AM-2 PM)',
      'afternoon': 'Afternoon (2-6 PM)',
      'evening': 'Evening (6-9 PM)'
    };
    return timeMap[time] || time;
  };

  // Extract user profile data with defaults
  const profile = userProfile || {};
  const age = profile.age || 30;
  const gender = profile.gender || 'male';
  const height = formatHeight(profile.height);
  const primaryGoal = formatGoal(profile.primary_goal || profile.primaryGoal || 'strength');
  const targetAreas = Array.isArray(profile.target_areas) ? profile.target_areas :
                      Array.isArray(profile.targetAreas) ? profile.targetAreas :
                      [];
  const fitnessLevel = profile.fitness_level || profile.fitnessLevel || 'intermediate';
  const timeframe = profile.timeframe || '12_weeks';
  const daysPerWeek = profile.days_per_week || profile.daysPerWeek || 5;
  const timePerSession = formatDuration(profile.time_per_session || profile.timePerSession);
  const homeWorkoutTime = formatTime(profile.home_workout_time || profile.homeWorkoutTime || 'early_morning');
  const gymWorkoutTime = formatTime(profile.gym_workout_time || profile.gymWorkoutTime || 'morning');
  const gymDays = Array.isArray(profile.gym_days) ? profile.gym_days :
                  Array.isArray(profile.gymDays) ? profile.gymDays :
                  [];
  const restDays = Array.isArray(profile.rest_days) ? profile.rest_days :
                   Array.isArray(profile.restDays) ? profile.restDays :
                   [];
  const gymAccess = Array.isArray(profile.gym_access) ? profile.gym_access :
                    Array.isArray(profile.gymAccess) ? profile.gymAccess :
                    [];
  const equipment = Array.isArray(profile.equipment) ? profile.equipment : [];

  // Calculate goal targets based on current measurements
  const currentPullups = measurements.pullups || 0;
  const currentPushups = measurements.pushups || 0;
  const currentBodyFat = measurements.bodyFat || 20;
  const targetPullups = Math.max(10, Math.ceil(currentPullups * 1.5));
  const targetPushups = Math.max(30, Math.ceil(currentPushups * 1.5));
  const targetBodyFat = Math.max(12, currentBodyFat - 5);

  // Determine gym vs home days
  const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const workoutDays = allDays.filter(day => !restDays.includes(day));
  const homeDays = workoutDays.filter(day => !gymDays.includes(day));

  // Calculate total progress since start (profile = starting point)
  const startWeight = profile.weight || measurements.weight || 175;
  const startBodyFat = profile.body_fat || profile.bodyFat || measurements.bodyFat || 20;
  const startPullups = profile.pullups || currentPullups;
  const startPushups = profile.pushups || currentPushups;

  const weightChange = (measurements.weight || startWeight) - startWeight;
  const bodyFatChange = (measurements.bodyFat || startBodyFat) - startBodyFat;
  const pullupsGained = currentPullups - startPullups;
  const pushupsGained = currentPushups - startPushups;

  // Calculate performance metrics from last week's workouts
  let avgDifficulty = 0;
  let totalSets = 0;
  let completedSets = 0;
  let totalVolume = 0;
  const exerciseDifficulties = {};

  lastWeekWorkouts.forEach(workout => {
    workout.exercises?.forEach(ex => {
      if (ex.difficulty) {
        exerciseDifficulties[ex.name] = exerciseDifficulties[ex.name] || [];
        exerciseDifficulties[ex.name].push(ex.difficulty);
      }
      if (ex.sets && ex.sets.length > 0) {
        ex.sets.forEach(set => {
          totalSets++;
          if (set.reps > 0) completedSets++;
          totalVolume += (set.weight || 0) * (set.reps || 0);
        });
      }
    });
  });

  // Calculate average difficulty
  const allDifficulties = Object.values(exerciseDifficulties).flat();
  avgDifficulty = allDifficulties.length > 0
    ? (allDifficulties.reduce((sum, d) => sum + d, 0) / allDifficulties.length).toFixed(1)
    : 'N/A';

  const completionRate = totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

  // Identify exercises getting easier (difficulty < 6)
  const easyExercises = Object.entries(exerciseDifficulties)
    .filter(([_, difficulties]) => {
      const avg = difficulties.reduce((sum, d) => sum + d, 0) / difficulties.length;
      return avg < 6;
    })
    .map(([name]) => name);

  // Identify challenging exercises (difficulty >= 8)
  const hardExercises = Object.entries(exerciseDifficulties)
    .filter(([_, difficulties]) => {
      const avg = difficulties.reduce((sum, d) => sum + d, 0) / difficulties.length;
      return avg >= 8;
    })
    .map(([name]) => name);

  return `You are ${profile.display_name || 'the user'}'s personal strength coach. Your job is to design next week's training program based on last week's performance and total progress since starting.

**USER'S PROFILE:**
- ${age}-year-old ${gender}, ${height}
- Fitness Level: ${fitnessLevel}
- Primary Goal: ${primaryGoal}
${targetAreas.length > 0 ? `- Target Areas: ${targetAreas.join(', ')}` : ''}
- Current: ${measurements.weight || 175} lbs, ${measurements.bodyFat || 20}% body fat
- Current fitness: ${currentPullups} pull-ups, ${currentPushups} push-ups max

${tier4SinceStart ? `
**TRAINING TIMELINE:**
- Program Start: ${tier4SinceStart.programStartDate}
- Weeks Completed: ${tier4SinceStart.weeksCompleted}
- Total Workouts: ${tier4SinceStart.totalWorkoutsCompleted}
- Overall Adherence: ${tier4SinceStart.overallAdherenceRate}% ${tier4SinceStart.overallAdherenceRate < 70 ? '(⚠️ low - discuss barriers)' : tier4SinceStart.overallAdherenceRate > 90 ? '(💪 excellent consistency!)' : '(good)'}
` : ''}

**TOTAL PROGRESS SINCE START:**
- Weight: ${startWeight} → ${measurements.weight || startWeight} lbs (${weightChange >= 0 ? '+' : ''}${weightChange.toFixed(1)} lbs)${tier4SinceStart ? ` (avg ${tier4SinceStart.progressRate.weightPerWeek} lbs/week)` : ''}
- Body Fat: ${startBodyFat}% → ${measurements.bodyFat || startBodyFat}% (${bodyFatChange >= 0 ? '+' : ''}${bodyFatChange.toFixed(1)}%)${tier4SinceStart ? ` (avg ${tier4SinceStart.progressRate.bodyFatPerMonth}%/month)` : ''}
- Pull-ups: ${startPullups} → ${currentPullups} reps (${pullupsGained >= 0 ? '+' : ''}${pullupsGained} reps)
- Push-ups: ${startPushups} → ${currentPushups} reps (${pushupsGained >= 0 ? '+' : ''}${pushupsGained} reps)

${tier3Last12Weeks && tier3Last12Weeks.monthlyCheckpoints.length > 0 ? `
**12-WEEK BODY COMPOSITION MILESTONES:**
${tier3Last12Weeks.monthlyCheckpoints.map((checkpoint, i) => `Month ${i + 1} (${checkpoint.date}): ${checkpoint.weight} lbs, ${checkpoint.bodyFat}% BF, ${checkpoint.fatFreeMass} lbs lean mass`).join('\n')}
` : ''}

${tier3Last12Weeks && (tier3Last12Weeks.exercisesProgressing.length > 0 || tier3Last12Weeks.exercisesPlateaued.length > 0) ? `
**EXERCISE PROGRESSION STATUS (Last 12 Weeks):**
${tier3Last12Weeks.exercisesProgressing.length > 0 ? `Progressing Well (${tier3Last12Weeks.exercisesProgressing.length} exercises):
${tier3Last12Weeks.exercisesProgressing.map(ex => `- ${ex.name}: ${ex.firstPerformance} → ${ex.lastPerformance} (+${ex.percentChange}% over ${ex.sessions} sessions)`).join('\n')}
` : ''}${tier3Last12Weeks.exercisesPlateaued.length > 0 ? `
⚠️ Plateaued (${tier3Last12Weeks.exercisesPlateaued.length} exercises - NEED VARIATION):
${tier3Last12Weeks.exercisesPlateaued.map(ex => `- ${ex.name}: ${ex.lastPerformance} (stuck for ${ex.sessions} sessions, avg difficulty ${ex.avgDifficulty}/10)`).join('\n')}
` : ''}` : ''}

${tier2Last4Weeks && tier2Last4Weeks.length > 0 ? `
**4-WEEK PERFORMANCE TRENDS:**
${tier2Last4Weeks.map(week => `Week ${week.weekNumber}: ${week.workoutsCompleted} workouts, ${week.totalVolume.toLocaleString()} lbs volume${week.avgDifficulty ? `, avg difficulty ${week.avgDifficulty}/10` : ''}, ${week.adherenceRate}% completion`).join('\n')}

Trend Analysis:
- Volume: ${tier2Last4Weeks[0].totalVolume < tier2Last4Weeks[3].totalVolume ? `📈 Increasing (${((tier2Last4Weeks[3].totalVolume - tier2Last4Weeks[0].totalVolume) / tier2Last4Weeks[0].totalVolume * 100).toFixed(1)}% over 4 weeks)` : tier2Last4Weeks[0].totalVolume > tier2Last4Weeks[3].totalVolume ? `📉 Decreasing (may indicate fatigue)` : `➡️ Stable`}
- Adherence: ${tier2Last4Weeks.map(w => w.adherenceRate).reduce((sum, rate) => sum + rate, 0) / 4}% average (${tier2Last4Weeks[3].adherenceRate > tier2Last4Weeks[0].adherenceRate ? 'improving' : tier2Last4Weeks[3].adherenceRate < tier2Last4Weeks[0].adherenceRate ? 'declining' : 'consistent'})
` : ''}

**GOALS & TIMELINE:**
- Timeframe: ${timeframe.replace('_', ' ')}
- Pull-ups: ${currentPullups} → ${targetPullups} strict pull-ups
- Push-ups: ${currentPushups} → ${targetPushups} in one set
- Body fat: ${currentBodyFat}% → ${targetBodyFat}%

**EQUIPMENT & SCHEDULE:**
- Training Days: ${daysPerWeek} days per week
- Workout Duration: ${timePerSession}
${gymDays.length > 0 ? `- Gym Days: ${gymDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')} at ${gymWorkoutTime}` : ''}
${gymAccess.length > 0 ? `  → Gym Access: ${gymAccess.map(g => g.replace(/_/g, ' ')).join(', ')}` : ''}
${homeDays.length > 0 ? `- Home Days: ${homeDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')} at ${homeWorkoutTime}` : ''}
${equipment.length > 0 && !equipment.includes('none') ? `  → Home Equipment Available: ${equipment.filter(e => e !== 'none').map(e => e.replace(/_/g, ' ')).join(', ')}` : ''}
${equipment.includes('none') || equipment.length === 0 ? `  → No home equipment (bodyweight only)` : ''}
${restDays.length > 0 ? `- Rest Days: ${restDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}` : ''}

**LAST WEEK'S PERFORMANCE:**
${formatLastWeekWorkouts(lastWeekWorkouts)}

${missedWorkouts.length > 0 ? `**MISSED WORKOUTS:** ${missedWorkouts.join(', ')} (${Math.round((1 - missedWorkouts.length / daysPerWeek) * 100)}% adherence rate)` : '**All workouts completed! 💪 (100% adherence)**'}

**PERFORMANCE ANALYSIS:**
- Average Difficulty Rating: ${avgDifficulty}/10${avgDifficulty !== 'N/A' ? (parseFloat(avgDifficulty) < 6 ? ' (exercises may be too easy - consider progression)' : parseFloat(avgDifficulty) > 8 ? ' (very challenging - good intensity)' : ' (appropriate challenge)') : ''}
- Set Completion Rate: ${completionRate}%${completionRate < 80 ? ' (consider reducing volume or weight)' : completionRate === 100 ? ' (perfect execution!)' : ' (good consistency)'}
- Total Volume Lifted: ${totalVolume.toLocaleString()} lbs
${easyExercises.length > 0 ? `- Exercises Getting Easier (consider weight increase): ${easyExercises.join(', ')}` : ''}
${hardExercises.length > 0 ? `- Still Challenging Exercises (maintain weight, add assistance work): ${hardExercises.join(', ')}` : ''}

**THIS WEEK'S CHECK-IN:**
${currentCheckIn ? formatCurrentCheckIn(currentCheckIn) : 'No check-in data provided this week.'}

${currentCheckIn && (currentCheckIn.bloodPressureSystolic || currentCheckIn.restingHeartRate) ? `
**CARDIOVASCULAR HEALTH:**
${currentCheckIn.bloodPressureSystolic ? `- Blood Pressure: ${currentCheckIn.bloodPressureSystolic}/${currentCheckIn.bloodPressureDiastolic} mmHg ${currentCheckIn.bloodPressureSystolic > 130 || currentCheckIn.bloodPressureDiastolic > 80 ? '(ELEVATED - consider reducing intensity, add more rest)' : currentCheckIn.bloodPressureSystolic < 120 && currentCheckIn.bloodPressureDiastolic < 80 ? '(OPTIMAL - can maintain or increase intensity)' : '(NORMAL - appropriate intensity)'}` : ''}
${currentCheckIn.restingHeartRate ? `- Resting Heart Rate: ${currentCheckIn.restingHeartRate} BPM ${currentCheckIn.restingHeartRate < 60 ? '(EXCELLENT conditioning)' : currentCheckIn.restingHeartRate > 80 ? '(consider adding more cardio, ensure adequate recovery)' : '(GOOD conditioning)'}` : ''}
` : ''}

${nutritionSummary && nutritionSummary.daysLogged > 0 ? `
**NUTRITION COMPLIANCE (LAST WEEK):**
- Days Logged: ${nutritionSummary.daysLogged}/7 ${nutritionSummary.daysLogged < 5 ? '(⚠️ inconsistent tracking - encourage daily logging)' : '(✓ good tracking consistency)'}
- Average Daily Calories: ${nutritionSummary.avgCalories} (goal: ${nutritionGoals.calorieGoal}) ${nutritionSummary.calorieVariance !== 0 ? `(${nutritionSummary.calorieVariance > 0 ? '+' : ''}${nutritionSummary.calorieVariance} ${nutritionSummary.calorieVariance < -200 ? '⚠️ significantly under - may affect recovery' : nutritionSummary.calorieVariance > 200 ? '⚠️ significantly over - adjust portions' : ''})` : '(on target)'}
- Average Daily Protein: ${nutritionSummary.avgProtein}g (goal: ${nutritionGoals.proteinGoal}g) ${nutritionSummary.proteinVariance !== 0 ? `(${nutritionSummary.proteinVariance > 0 ? '+' : ''}${nutritionSummary.proteinVariance}g ${nutritionSummary.proteinVariance < -30 ? '⚠️ protein too low for muscle building' : nutritionSummary.proteinVariance > 30 ? '✓ excellent protein intake' : ''})` : '(on target)'}
- Days On Target: ${nutritionSummary.daysOnTarget}/${nutritionSummary.daysLogged} (${Math.round((nutritionSummary.daysOnTarget / nutritionSummary.daysLogged) * 100)}% compliance)${nutritionSummary.daysOnTarget < 3 ? ' ⚠️ Low compliance - discuss realistic targets' : ''}

${nutritionSummary.calorieVariance < -200 && missedWorkouts.length > 0 ? '⚠️ **ALERT**: Low calories + missed workouts = under-recovered. Consider deload or extra rest.\n' : ''}${nutritionSummary.proteinVariance < -30 && (measurements.fatFreeMass || currentCheckIn?.fatFreeMass) ? '⚠️ **ALERT**: Low protein may explain muscle loss or lack of strength gains.\n' : ''}
` : nutritionSummary ? '\n**NUTRITION:** No nutrition data logged last week. Encourage user to track calories and protein.\n' : ''}

**BODY MEASUREMENTS TREND:**
${formatMeasurementTrend(measurements, measurementHistory, currentCheckIn)}

${currentCheckIn && (currentCheckIn.bicepLeft || currentCheckIn.chest || currentCheckIn.waist || currentCheckIn.thighLeft) ? `
**MUSCLE MEASUREMENT ANALYSIS:**
${currentCheckIn.bicepLeft && currentCheckIn.bicepRight ? `- Arms: L:${currentCheckIn.bicepLeft}" / R:${currentCheckIn.bicepRight}" ${Math.abs(currentCheckIn.bicepLeft - currentCheckIn.bicepRight) > 0.25 ? '(⚠️ imbalance detected - add unilateral work)' : '(✓ balanced)'}` : ''}
${currentCheckIn.chest ? `- Chest: ${currentCheckIn.chest}" ${targetAreas.includes('chest') ? '(target area - continue emphasis)' : ''}` : ''}
${currentCheckIn.waist ? `- Waist: ${currentCheckIn.waist}" ${currentCheckIn.waist > 40 && gender === 'male' ? '(above 40" - prioritize fat loss)' : currentCheckIn.waist > 35 && gender === 'female' ? '(above 35" - prioritize fat loss)' : '(healthy range)'}` : ''}
${currentCheckIn.thighLeft && currentCheckIn.thighRight ? `- Thighs: L:${currentCheckIn.thighLeft}" / R:${currentCheckIn.thighRight}" ${Math.abs(currentCheckIn.thighLeft - currentCheckIn.thighRight) > 0.5 ? '(⚠️ imbalance detected - add unilateral leg work)' : targetAreas.includes('thighs') ? '(target area - continue emphasis)' : ''}` : ''}
` : ''}

---

**YOUR TASK:**
Design a complete 7-day training program for next week that considers ALL the data above. You MUST:

1. **Use Historical Trends to Guide Programming**:
${tier3Last12Weeks && tier3Last12Weeks.exercisesPlateaued.length > 0 ? `   - ⚠️ ${tier3Last12Weeks.exercisesPlateaued.length} exercises have plateaued: REPLACE with similar movement patterns or change rep ranges\n` : ''}${tier2Last4Weeks && tier2Last4Weeks.length > 0 && tier2Last4Weeks[0].totalVolume > tier2Last4Weeks[3].totalVolume * 1.1 ? `   - ⚠️ Volume decreasing over 4 weeks: Indicates fatigue. Consider deload or reducing sets\n` : ''}${tier4SinceStart && tier4SinceStart.overallAdherenceRate < 70 ? `   - ⚠️ Overall adherence only ${tier4SinceStart.overallAdherenceRate}%: Simplify program, reduce frequency\n` : ''}   - Exercises showing long-term progress (12 weeks): Continue with progressive overload
   - If 4-week volume trend is flat or declining despite effort: Time for deload or variation

2. **Progressive Overload Based on Performance Analysis**:
   - For exercises marked "Getting Easier": Increase weight by 5-10 lbs or add 2-3 reps
   - For exercises with difficulty 8-10: Maintain weight but aim for more reps
   - For exercises with difficulty 5-7: Keep weight same, add 1-2 reps
   - For exercises with difficulty 1-4 or <80% completion rate: Reduce weight, focus on form

3. **Address Imbalances & Weak Points**:
${currentCheckIn && currentCheckIn.bicepLeft && Math.abs(currentCheckIn.bicepLeft - currentCheckIn.bicepRight) > 0.25 ? '   - ADD unilateral arm work to address bicep imbalance\n' : ''}${currentCheckIn && currentCheckIn.thighLeft && Math.abs(currentCheckIn.thighLeft - currentCheckIn.thighRight) > 0.5 ? '   - ADD unilateral leg work to address thigh imbalance\n' : ''}   - If pull-ups or push-ups aren't progressing, add assistance exercises

4. **Adjust for Health Metrics**:
${currentCheckIn && currentCheckIn.bloodPressureSystolic > 130 ? '   - ⚠️ Blood pressure elevated: Reduce intensity, add more rest between sets, limit max effort\n' : ''}${currentCheckIn && currentCheckIn.restingHeartRate > 80 ? '   - ⚠️ Elevated resting HR: Ensure adequate recovery, consider extra rest day or active recovery\n' : ''}   - Account for user's age (${age} years) - adjust recovery time accordingly

5. **Manage Volume Based on Adherence**:
${missedWorkouts.length > 0 ? `   - ⚠️ ${missedWorkouts.length} workouts missed last week: Start conservative to prevent injury\n` : '   - 100% adherence: Can maintain or slightly increase volume\n'}

6. **Prioritize User's Specific Goals**:
   - Primary Goal: ${primaryGoal}
${targetAreas.length > 0 ? `   - Target Areas: ${targetAreas.join(', ')} - ensure these get adequate volume\n` : ''}   - Timeframe: ${timeframe.replace('_', ' ')} - pace progression appropriately

7. **Mix Equipment Strategically**:
   - Include bodyweight exercises even when equipment is available (for variety and functional movement)
   - Use progressive calisthenics to build foundational strength
   - Don't exclusively rely on weights - mix bodyweight, resistance, and weighted exercises

8. **Respect the Schedule**:
${gymDays.length > 0 ? `   - Gym days (${gymDays.join(', ')}): Use ${gymAccess.join(', ')} equipment\n` : ''}${homeDays.length > 0 ? `   - Home days (${homeDays.join(', ')}): ${equipment.length > 0 && !equipment.includes('none') ? `Utilize ${equipment.filter(e => e !== 'none').join(', ')} + bodyweight` : 'Bodyweight exercises'}\n` : ''}${restDays.length > 0 ? `   - Rest days (${restDays.join(', ')}): Complete rest or light active recovery\n` : ''}

9. **Adjust Based on Nutrition Compliance**:
${nutritionSummary && nutritionSummary.daysLogged > 0 ? `   - Average calories: ${nutritionSummary.avgCalories} vs goal: ${nutritionGoals.calorieGoal}
${nutritionSummary.calorieVariance < -200 ? '   - ⚠️ SIGNIFICANTLY under calories: User may be under-recovered. Reduce volume or intensity.\n' : nutritionSummary.calorieVariance > 200 ? '   - ⚠️ Over calorie goal: If fat loss is goal, suggest meal prep strategies.\n' : '   - Calories on target - maintain current approach.\n'}${nutritionSummary.proteinVariance < -30 ? '   - ⚠️ Protein too low: Emphasize importance of protein for muscle recovery. Suggest high-protein meals.\n' : nutritionSummary.proteinVariance > 30 ? '   - ✓ Excellent protein intake - user is prioritizing recovery well.\n' : '   - Protein adequate - continue current intake.\n'}${nutritionSummary.daysOnTarget < 3 ? '   - ⚠️ Low compliance: Goals may be unrealistic. Consider adjusting calorie/protein targets.\n' : ''}` : '   - No nutrition data available. Encourage user to track calories and protein for better results.\n'}

**OUTPUT FORMAT:**
Return the workout plan as a structured JSON object. Use this exact format:

\`\`\`json
{
  "weekSummary": "2-3 sentence summary of the plan and why you made these adjustments",
  "monday": {
    "name": "Workout Name",
    "time": "${gymDays.includes('monday') ? gymWorkoutTime : homeWorkoutTime}",
    "duration": "${timePerSession}",
    "location": "${gymDays.includes('monday') ? (gymAccess.length > 0 ? gymAccess[0].replace(/_/g, ' ') : 'Gym') : 'Home'}",
    "focus": "Body parts focused",
    "exercises": [
      {
        "id": 1,
        "name": "Exercise Name",
        "sets": 4,
        "reps": "10-12",
        "rest": "90 sec",
        "notes": "Detailed form cues and why this exercise",
        "progressionTips": "How to progress this exercise"
      }
    ]
  },
  "tuesday": { ... },
  "wednesday": { ... },
  "thursday": { ... },
  "friday": { ... },
  "saturday": { ... },
  "sunday": { ... }
}
\`\`\`

IMPORTANT:
- Return ONLY valid JSON, no other text before or after
${gymAccess.length > 0 ? `- For gym days (${gymDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}), use equipment available at: ${gymAccess.map(g => g.replace(/_/g, ' ')).join(', ')}` : ''}
${homeDays.length > 0 && equipment.length > 0 && !equipment.includes('none') ? `- For home days (${homeDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}), MUST utilize available equipment: ${equipment.filter(e => e !== 'none').map(e => e.replace(/_/g, ' ')).join(', ')}` : ''}
${homeDays.length > 0 && (equipment.length === 0 || equipment.includes('none')) ? `- ⚠️ CRITICAL: For home days (${homeDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}), use ONLY bodyweight exercises (no equipment available). DO NOT include exercises requiring gym machines, barbells, cables, or any equipment not listed above.` : ''}
- Include 5-8 exercises per workout based on ${timePerSession}
- Be specific about weights to try (e.g., "Try 145 lbs based on last week's 140 @ difficulty 8")
- For rest days, provide "name": "Rest Day", "exercises": []
- ⚠️ STRICT RULE: Home workouts MUST NOT include any gym-only equipment (machines, cable systems, smith machines, etc.). Only use: ${homeDays.length > 0 && (equipment.length === 0 || equipment.includes('none')) ? 'bodyweight exercises' : equipment.length > 0 && !equipment.includes('none') ? equipment.filter(e => e !== 'none').map(e => e.replace(/_/g, ' ')).join(', ') + ' and bodyweight exercises' : 'as specified above'}.`;
}

function formatLastWeekWorkouts(workouts) {
  if (!workouts || workouts.length === 0) {
    return 'No workout data from last week. Start with baseline program.';
  }

  return workouts.map(workout => {
    const day = new Date(workout.date).toLocaleDateString('en-US', { weekday: 'long' });
    const exerciseDetails = workout.exercises.map(ex => {
      if (!ex.sets || ex.sets.length === 0) {
        return `  - ${ex.name}: Not completed`;
      }

      const setsStr = ex.sets.map(s => `${s.weight}lbs × ${s.reps}`).join(', ');
      const diffStr = ex.difficulty ? ` | Difficulty: ${ex.difficulty}/10` : '';
      const completedStr = ex.completed ? '✓' : '✗';

      return `  ${completedStr} ${ex.name}: ${setsStr}${diffStr}`;
    }).join('\n');

    return `**${day} - ${workout.workoutName}**\n${exerciseDetails}`;
  }).join('\n\n');
}

function formatCurrentCheckIn(checkIn) {
  return `**Weight:** ${checkIn.weight} lbs | **Body Fat:** ${checkIn.bodyFat}% | **Fat-Free Mass:** ${checkIn.fatFreeMass} lbs
**Blood Pressure:** ${checkIn.bloodPressureSystolic || 'N/A'}/${checkIn.bloodPressureDiastolic || 'N/A'} mmHg | **Resting HR:** ${checkIn.restingHeartRate || 'N/A'} BPM
**Biceps:** L: ${checkIn.bicepLeft || 'N/A'}", R: ${checkIn.bicepRight || 'N/A'}" | **Chest:** ${checkIn.chest || 'N/A'}" | **Waist:** ${checkIn.waist || 'N/A'}" | **Thighs:** L: ${checkIn.thighLeft || 'N/A'}", R: ${checkIn.thighRight || 'N/A'}"
${checkIn.notes ? `**Notes:** ${checkIn.notes}` : ''}`;
}

function formatMeasurementTrend(current, history, checkIn) {
  const latestMeasurement = checkIn || current;

  if (!history || history.length === 0) {
    return `Current: ${latestMeasurement.weight || 175} lbs, ${latestMeasurement.bodyFat || 20}% body fat
No historical data available - this is the first measurement.`;
  }

  const recentHistory = history.slice(-4); // Last 4 measurements
  if (recentHistory.length === 0) {
    return `Current: ${latestMeasurement.weight} lbs, ${latestMeasurement.bodyFat}% body fat
Starting baseline measurement.`;
  }

  const oldest = recentHistory[0];

  const weightChange = latestMeasurement.weight - oldest.weight;
  const bodyFatChange = latestMeasurement.bodyFat - oldest.bodyFat;
  const ffmChange = latestMeasurement.fatFreeMass && oldest.fatFreeMass
    ? latestMeasurement.fatFreeMass - oldest.fatFreeMass
    : null;

  return `**Progress Over Last ${recentHistory.length} Weeks:**
Weight: ${oldest.weight} → ${latestMeasurement.weight} lbs (${weightChange >= 0 ? '+' : ''}${weightChange.toFixed(1)} lbs)
Body Fat: ${oldest.bodyFat}% → ${latestMeasurement.bodyFat}% (${bodyFatChange >= 0 ? '+' : ''}${bodyFatChange.toFixed(1)}%)
${ffmChange !== null ? `Fat-Free Mass: ${ffmChange >= 0 ? '+' : ''}${ffmChange.toFixed(1)} lbs (${ffmChange > 0 ? 'GAINING MUSCLE! 💪' : 'muscle loss - adjust nutrition'})` : ''}

**Latest Measurements:**
Biceps: L: ${latestMeasurement.bicepLeft || 'N/A'}", R: ${latestMeasurement.bicepRight || 'N/A'}"
Chest: ${latestMeasurement.chest || 'N/A'}"
Waist: ${latestMeasurement.waist || 'N/A'}"
Thighs: L: ${latestMeasurement.thighLeft || 'N/A'}", R: ${latestMeasurement.thighRight || 'N/A'}"`;
}

// ==================== 4-TIER HISTORICAL DATA SYSTEM ====================

/**
 * TIER 1: Last Week Detailed Analysis
 * Full workout data for immediate progressive overload decisions
 */
function getTier1LastWeekDetailed(workouts) {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const lastWeekWorkouts = workouts.filter(w => new Date(w.date) >= oneWeekAgo);

  // Calculate performance metrics
  let totalVolume = 0;
  let totalSets = 0;
  let completedSets = 0;
  const exerciseDifficulties = {};

  lastWeekWorkouts.forEach(workout => {
    workout.exercises?.forEach(ex => {
      if (ex.difficulty) {
        exerciseDifficulties[ex.name] = exerciseDifficulties[ex.name] || [];
        exerciseDifficulties[ex.name].push(ex.difficulty);
      }
      if (ex.sets && ex.sets.length > 0) {
        ex.sets.forEach(set => {
          totalSets++;
          if (set.reps > 0) completedSets++;
          totalVolume += (set.weight || 0) * (set.reps || 0);
        });
      }
    });
  });

  const avgDifficulty = Object.values(exerciseDifficulties).flat();
  const avgDiff = avgDifficulty.length > 0
    ? (avgDifficulty.reduce((sum, d) => sum + d, 0) / avgDifficulty.length).toFixed(1)
    : 'N/A';

  return {
    workouts: lastWeekWorkouts,
    totalVolume,
    avgDifficulty: avgDiff,
    completionRate: totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0
  };
}

/**
 * TIER 2: Last 4 Weeks Trend Analysis
 * Weekly summaries for recent pattern detection
 */
function getTier2Last4WeeksTrends(workouts) {
  const now = new Date();
  const weeklyData = [];

  // Get last 4 weeks of data
  for (let weekOffset = 0; weekOffset < 4; weekOffset++) {
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - (7 * (weekOffset + 1)));
    const weekEnd = new Date(now);
    weekEnd.setDate(now.getDate() - (7 * weekOffset));

    const weekWorkouts = workouts.filter(w => {
      const workoutDate = new Date(w.date);
      return workoutDate >= weekStart && workoutDate < weekEnd;
    });

    // Calculate volume for this week
    let weekVolume = 0;
    let weekSets = 0;
    let weekCompletedSets = 0;
    const weekDifficulties = [];

    weekWorkouts.forEach(workout => {
      workout.exercises?.forEach(ex => {
        if (ex.difficulty) weekDifficulties.push(ex.difficulty);
        if (ex.sets && ex.sets.length > 0) {
          ex.sets.forEach(set => {
            weekSets++;
            if (set.reps > 0) weekCompletedSets++;
            weekVolume += (set.weight || 0) * (set.reps || 0);
          });
        }
      });
    });

    const avgDiff = weekDifficulties.length > 0
      ? (weekDifficulties.reduce((sum, d) => sum + d, 0) / weekDifficulties.length).toFixed(1)
      : null;

    weeklyData.unshift({ // Add to beginning so week 1 is oldest
      weekNumber: 4 - weekOffset,
      workoutsCompleted: weekWorkouts.length,
      totalVolume: weekVolume,
      avgDifficulty: avgDiff,
      adherenceRate: weekSets > 0 ? Math.round((weekCompletedSets / weekSets) * 100) : 0
    });
  }

  return weeklyData;
}

/**
 * TIER 3: Last 12 Weeks Key Milestones
 * Exercise-specific progress and monthly checkpoints
 */
function getTier3Last12WeeksMilestones(workouts, measurementHistory) {
  const now = new Date();
  const twelveWeeksAgo = new Date(now);
  twelveWeeksAgo.setDate(now.getDate() - 84); // 12 weeks = 84 days

  const recentWorkouts = workouts.filter(w => new Date(w.date) >= twelveWeeksAgo);

  // Track exercise-specific progress
  const exerciseProgress = {};

  recentWorkouts.forEach(workout => {
    workout.exercises?.forEach(ex => {
      if (!ex.sets || ex.sets.length === 0) return;

      if (!exerciseProgress[ex.name]) {
        exerciseProgress[ex.name] = {
          firstSeen: new Date(workout.date),
          lastSeen: new Date(workout.date),
          firstWeight: null,
          lastWeight: null,
          firstReps: null,
          lastReps: null,
          sessions: 0,
          avgDifficulty: []
        };
      }

      const progress = exerciseProgress[ex.name];
      progress.sessions++;
      progress.lastSeen = new Date(workout.date);

      if (ex.difficulty) progress.avgDifficulty.push(ex.difficulty);

      // Track first and last performance
      ex.sets.forEach(set => {
        if (set.weight && set.reps) {
          if (!progress.firstWeight) {
            progress.firstWeight = set.weight;
            progress.firstReps = set.reps;
          }
          progress.lastWeight = set.weight;
          progress.lastReps = set.reps;
        }
      });
    });
  });

  // Identify progressing vs plateaued exercises
  const progressing = [];
  const plateaued = [];

  Object.entries(exerciseProgress).forEach(([exerciseName, progress]) => {
    if (!progress.firstWeight || !progress.lastWeight) return;

    const volumeChange = (progress.lastWeight * progress.lastReps) - (progress.firstWeight * progress.firstReps);
    const percentChange = (volumeChange / (progress.firstWeight * progress.firstReps)) * 100;

    const avgDiff = progress.avgDifficulty.length > 0
      ? (progress.avgDifficulty.reduce((sum, d) => sum + d, 0) / progress.avgDifficulty.length).toFixed(1)
      : null;

    const exerciseData = {
      name: exerciseName,
      firstPerformance: `${progress.firstWeight} lbs × ${progress.firstReps}`,
      lastPerformance: `${progress.lastWeight} lbs × ${progress.lastReps}`,
      percentChange: percentChange.toFixed(1),
      sessions: progress.sessions,
      avgDifficulty: avgDiff
    };

    if (percentChange > 5) {
      progressing.push(exerciseData);
    } else if (percentChange < 2 && progress.sessions >= 6) {
      plateaued.push(exerciseData);
    }
  });

  // Get monthly body composition checkpoints
  const monthlyCheckpoints = [];
  if (measurementHistory && measurementHistory.length > 0) {
    // Group measurements by month (roughly every 4 weeks)
    for (let i = 0; i < measurementHistory.length; i += 4) {
      const measurement = measurementHistory[i];
      monthlyCheckpoints.push({
        date: measurement.date,
        weight: measurement.weight,
        bodyFat: measurement.bodyFat,
        fatFreeMass: measurement.fatFreeMass
      });
    }
  }

  return {
    exercisesProgressing: progressing,
    exercisesPlateaued: plateaued,
    monthlyCheckpoints
  };
}

/**
 * TIER 4: Since Start Total Progress Summary
 * Full journey context and major achievements
 */
function getTier4SinceStartSummary(userProfile, currentMeasurements, measurementHistory, workouts) {
  // Calculate program start date (earliest workout or measurement)
  let programStartDate = null;

  if (workouts.length > 0) {
    const sortedWorkouts = [...workouts].sort((a, b) => new Date(a.date) - new Date(b.date));
    programStartDate = new Date(sortedWorkouts[0].date);
  }

  if (measurementHistory && measurementHistory.length > 0) {
    const sortedMeasurements = [...measurementHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstMeasurementDate = new Date(sortedMeasurements[0].date);

    if (!programStartDate || firstMeasurementDate < programStartDate) {
      programStartDate = firstMeasurementDate;
    }
  }

  // If no workout/measurement history, use profile creation date or fallback
  if (!programStartDate) {
    programStartDate = new Date(userProfile?.created_at || Date.now());
  }

  // Calculate weeks completed
  const now = new Date();
  const weeksSinceStart = Math.floor((now - programStartDate) / (7 * 24 * 60 * 60 * 1000));

  // Starting values from profile
  const startWeight = userProfile?.weight || currentMeasurements.weight || 175;
  const startBodyFat = userProfile?.body_fat || userProfile?.bodyFat || currentMeasurements.bodyFat || 20;
  const startPullups = userProfile?.pullups || 0;
  const startPushups = userProfile?.pushups || 0;

  // Current values
  const currentWeight = currentMeasurements.weight || startWeight;
  const currentBodyFat = currentMeasurements.bodyFat || startBodyFat;
  const currentPullups = currentMeasurements.pullups || 0;
  const currentPushups = currentMeasurements.pushups || 0;

  // Calculate total changes
  const totalWeightChange = currentWeight - startWeight;
  const totalBodyFatChange = currentBodyFat - startBodyFat;
  const totalPullupGain = currentPullups - startPullups;
  const totalPushupGain = currentPushups - startPushups;

  // Calculate total workouts completed
  const totalWorkoutsCompleted = workouts.length;

  // Calculate total adherence rate (expected vs completed)
  const daysPerWeek = userProfile?.days_per_week || userProfile?.daysPerWeek || 5;
  const expectedWorkouts = weeksSinceStart * daysPerWeek;
  const overallAdherence = expectedWorkouts > 0
    ? Math.round((totalWorkoutsCompleted / expectedWorkouts) * 100)
    : 0;

  return {
    programStartDate: programStartDate.toISOString().split('T')[0],
    weeksCompleted: weeksSinceStart,
    totalWorkoutsCompleted,
    overallAdherenceRate: overallAdherence,
    startingMetrics: {
      weight: startWeight,
      bodyFat: startBodyFat,
      pullups: startPullups,
      pushups: startPushups
    },
    currentMetrics: {
      weight: currentWeight,
      bodyFat: currentBodyFat,
      pullups: currentPullups,
      pushups: currentPushups
    },
    totalProgress: {
      weight: totalWeightChange,
      bodyFat: totalBodyFatChange,
      pullups: totalPullupGain,
      pushups: totalPushupGain
    },
    progressRate: {
      weightPerWeek: weeksSinceStart > 0 ? (totalWeightChange / weeksSinceStart).toFixed(2) : 0,
      bodyFatPerMonth: weeksSinceStart > 0 ? ((totalBodyFatChange / weeksSinceStart) * 4).toFixed(2) : 0
    }
  };
}

/**
 * Main function: Gather all 4 tiers of historical data
 */
export function getLastWeekData() {
  // Get all workout history
  const detailedHistory = localStorage.getItem('detailedWorkoutHistory');
  const workouts = detailedHistory ? JSON.parse(detailedHistory) : [];

  // Get measurements
  const measurementsStr = localStorage.getItem('measurements');
  const measurements = measurementsStr ? JSON.parse(measurementsStr) : {};

  const historyStr = localStorage.getItem('measurementHistory');
  const measurementHistory = historyStr ? JSON.parse(historyStr) : [];

  // Get user profile (needed for Tier 4)
  const username = localStorage.getItem('currentUser');
  const profileStr = localStorage.getItem(`userProfile_${username}`);
  const userProfile = profileStr ? JSON.parse(profileStr) : {};

  // Gather all 4 tiers
  const tier1 = getTier1LastWeekDetailed(workouts);
  const tier2 = getTier2Last4WeeksTrends(workouts);
  const tier3 = getTier3Last12WeeksMilestones(workouts, measurementHistory);
  const tier4 = getTier4SinceStartSummary(userProfile, measurements, measurementHistory, workouts);

  // Determine missed workouts for last week
  const expectedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const completedDays = tier1.workouts.map(w => w.day);
  const missedWorkouts = expectedDays.filter(day => !completedDays.includes(day));

  return {
    // Legacy format (for backward compatibility)
    lastWeekWorkouts: tier1.workouts,
    missedWorkouts,
    measurements,
    measurementHistory,
    goals: {
      pullups: { current: measurements.pullups || 3, target: 10 },
      pushups: { current: measurements.pushups || 15, target: 30 },
      bodyFat: { current: measurements.bodyFat || 20, target: 15 }
    },

    // New 4-tier historical data
    tier1LastWeek: tier1,
    tier2Last4Weeks: tier2,
    tier3Last12Weeks: tier3,
    tier4SinceStart: tier4
  };
}
